import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import FormField from '../../../components/fields/FormField';
import InlineErrorState from '../../../components/states/InlineErrorState';
import PageCard from '../../../components/page-card';
import { useHelpCategories, HelpCategoryDto } from '../hooks/useHelpCategories';
import { useCreateHelpCategory, CreateHelpCategoryInput } from '../hooks/useCreateHelpCategory';
import { useUpdateHelpCategory, UpdateHelpCategoryInput } from '../hooks/useUpdateHelpCategory';
import { useDeleteHelpCategory } from '../hooks/useDeleteHelpCategory';
import { translate } from '../../../i18n';

const emptyForm: CreateHelpCategoryInput = {
  nameEn: '',
  nameFr: '',
  descriptionEn: '',
  descriptionFr: '',
  slug: '',
  icon: '',
  sortOrder: 0,
};

export default function HelpCategoryManager() {
  const toast = useToast();
  /**
   * `GET help/admin/categories` carries `@UseGuards(AdminAuthGuard)`
   * (`api/src/help-center/help-center.controller.ts`, read this run), so a
   * refusal — stale token, an operator who is not an admin — lands here on top
   * of the ordinary 5xx paths, and `data === undefined` left this page showing
   * an EMPTY TABLE with no message of any kind: the loading row is gated on
   * `isLoading` and the "No categories yet" row on `categories?.length === 0`,
   * which is false for `undefined`. An operator reading a blank list beside a
   * "New Category" button is one click from creating a duplicate of a category
   * that already exists (the slug is unique, so the duplicate fails at the API
   * with an error that explains nothing about why the list looked empty).
   *
   * `categoriesUnavailable` requires the data to be missing too: a failed
   * background refetch keeps the last good list on screen, and that list is
   * still true.
   */
  const { data: categories, isLoading, isError } = useHelpCategories();
  const categoriesUnavailable = isError && !categories;

  // Modal state
  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [form, setForm] = useState<CreateHelpCategoryInput>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const createMutation = useCreateHelpCategory(() => {
    createModal.onClose();
    setForm(emptyForm);
    toast({ title: translate('help_center.category_created'), status: 'success', duration: 3000 });
  });

  const updateMutation = useUpdateHelpCategory(() => {
    editModal.onClose();
    toast({ title: translate('help_center.category_updated'), status: 'success', duration: 3000 });
  });

  const deleteMutation = useDeleteHelpCategory(() => {
    deleteDialog.onClose();
    toast({ title: translate('help_center.category_deleted'), status: 'success', duration: 3000 });
  });

  const handleCreate = () => {
    createMutation.mutate(form);
  };

  const handleEdit = (cat: HelpCategoryDto) => {
    setEditId(cat.id);
    setForm({
      nameEn: cat.nameEn,
      nameFr: cat.nameFr,
      descriptionEn: cat.descriptionEn || '',
      descriptionFr: cat.descriptionFr || '',
      slug: cat.slug,
      icon: cat.icon || '',
      sortOrder: cat.sortOrder,
    });
    editModal.onOpen();
  };

  const handleUpdate = () => {
    if (!editId) return;
    updateMutation.mutate({ id: editId, ...form });
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    deleteMutation.mutate(deleteId);
  };

  const updateField = (field: keyof CreateHelpCategoryInput, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const slugify = (text: string) =>
    text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <Box p={6}>
      <PageCard
        showBackButton
        title={translate('help_center.manage_categories')}
        rightHeader={
          <Button
            leftIcon={<MdAdd />}
            colorScheme="brand"
            size="sm"
            onClick={() => {
              setForm(emptyForm);
              createModal.onOpen();
            }}
          >
            {translate('help_center.new_category')}
          </Button>
        }
      >
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Name (EN)</Th>
              <Th>Name (FR)</Th>
              <Th>Slug</Th>
              <Th>Icon</Th>
              <Th isNumeric>Order</Th>
              <Th width="100px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr><Td colSpan={6}><Text color="gray.500">Loading...</Text></Td></Tr>
            )}
            {categoriesUnavailable && (
              <Tr>
                <Td colSpan={6}>
                  <InlineErrorState message={translate('common.unexpected_error')} />
                </Td>
              </Tr>
            )}
            {categories?.map((cat) => (
              <Tr key={cat.id}>
                <Td>{cat.nameEn}</Td>
                <Td>{cat.nameFr}</Td>
                <Td><Badge variant="subtle" colorScheme="gray">{cat.slug}</Badge></Td>
                <Td>{cat.icon || '—'}</Td>
                <Td isNumeric>{cat.sortOrder}</Td>
                <Td>
                  <Flex gap={1}>
                    <IconButton
                      aria-label="Edit"
                      icon={<MdEdit />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(cat)}
                    />
                    <IconButton
                      aria-label="Delete"
                      icon={<MdDelete />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        setDeleteId(cat.id);
                        deleteDialog.onOpen();
                      }}
                    />
                  </Flex>
                </Td>
              </Tr>
            ))}
            {!categoriesUnavailable && !isLoading && categories?.length === 0 && (
              <Tr><Td colSpan={6}><Text color="gray.500" textAlign="center">No categories yet</Text></Td></Tr>
            )}
          </Tbody>
        </Table>
      </PageCard>

      {/* Create Modal */}
      <FormModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        title={translate('help_center.new_category')}
        onSubmit={handleCreate}
        isSubmitting={createMutation.isLoading}
        size="lg"
      >
        <CategoryForm form={form} updateField={updateField} slugify={slugify} isEditing={false} />
      </FormModal>

      {/* Edit Modal */}
      <FormModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        title="Edit Category"
        onSubmit={handleUpdate}
        isSubmitting={updateMutation.isLoading}
        size="lg"
      >
        <CategoryForm form={form} updateField={updateField} slugify={slugify} isEditing={true} />
      </FormModal>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete Category</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? Categories with articles cannot be deleted.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>Cancel</Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                isLoading={deleteMutation.isLoading}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

function CategoryForm({
  form,
  updateField,
  slugify,
  isEditing,
}: {
  form: CreateHelpCategoryInput;
  updateField: (field: keyof CreateHelpCategoryInput, value: any) => void;
  slugify: (text: string) => string;
  isEditing: boolean;
}) {
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(isEditing);

  return (
    <FormStack>
      <FormField
        type="text"
        label={translate('help_center.category_name_en')}
        value={form.nameEn}
        onChange={(e: any) => {
          const val = e.target.value;
          updateField('nameEn', val);
          if (!slugManuallyEdited) {
            updateField('slug', slugify(val));
          }
        }}
      />
      <FormField
        type="text"
        label={translate('help_center.category_name_fr')}
        value={form.nameFr}
        onChange={(e: any) => updateField('nameFr', e.target.value)}
      />
      <FormField
        type="text"
        label={translate('help_center.category_slug')}
        value={form.slug}
        onChange={(e: any) => {
          setSlugManuallyEdited(true);
          updateField('slug', e.target.value);
        }}
      />
      <FormField
        type="text"
        label="Description (EN)"
        value={form.descriptionEn || ''}
        onChange={(e: any) => updateField('descriptionEn', e.target.value)}
      />
      <FormField
        type="text"
        label="Description (FR)"
        value={form.descriptionFr || ''}
        onChange={(e: any) => updateField('descriptionFr', e.target.value)}
      />
      <FormField
        type="text"
        label="Icon"
        value={form.icon || ''}
        onChange={(e: any) => updateField('icon', e.target.value)}
        helperText="Material icon name (e.g. MdRocketLaunch)"
      />
      <FormField
        type="number"
        label="Sort Order"
        value={form.sortOrder ?? 0}
        onChange={(e: any) => updateField('sortOrder', parseInt(e.target.value) || 0)}
      />
    </FormStack>
  );
}
